/**
 * CookieConsent Component - vanilla-cookieconsent Integration
 * 
 * Dezenter, DSGVO-konformer Cookie-Banner für Billution
 * - Minimalistisches Design passend zum Corporate Design (Indigo/Purple)
 * - Google Consent Mode v2 Integration
 * - Deutsche und englische Texte
 * - Opt-in für Analytics (rechtlich erforderlich in der EU)
 */

import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import analyticsService from '../services/analyticsService';

/**
 * Leitet die Consent-Cookie-Domain aus dem aktuellen Hostname ab, damit ein
 * einmal auf faebill.com gesetzter Consent auch für app.faebill.com gilt
 * (registrierbare Domain statt Host-only). Gleiche Logik wie im Marketing-Banner.
 * - endet auf `faebill.com`   → `.faebill.com`
 * - endet auf `billution.com` → `.billution.com`
 * - sonst (localhost/Preview) → undefined ⇒ Host-only-Default der Library.
 */
function getConsentCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  if (host === 'faebill.com' || host.endsWith('.faebill.com')) return '.faebill.com';
  if (host === 'billution.com' || host.endsWith('.billution.com')) return '.billution.com';
  return undefined;
}

// Custom CSS für Billution-Branding
// M-D5 W3-Fix §8.3: --cc-*-Indirektion der Lib bleibt, Werte auf Design-Tokens
// umgestellt (Light/Dark-adaptiv statt hartkodierter Dark-Hexes).
const customStyles = `
  /* Billution Cookie Consent - Dezentes Design */
  :root {
    --cc-bg: var(--surface-raised);
    --cc-primary-color: var(--text-primary);
    --cc-secondary-color: var(--text-muted);
    --cc-btn-primary-bg: var(--accent);
    --cc-btn-primary-color: var(--accent-fg);
    --cc-btn-primary-hover-bg: #1BB377;
    --cc-btn-secondary-bg: var(--surface-inset);
    --cc-btn-secondary-color: var(--text-primary);
    --cc-btn-secondary-hover-bg: var(--surface-hover);
    --cc-toggle-on-bg: var(--accent);
    --cc-cookie-category-block-bg: var(--surface-inset);
    --cc-cookie-category-block-bg-hover: var(--surface-hover);
    --cc-overlay-bg: rgba(0, 0, 0, 0.65);
    --cc-separator-border-color: var(--border-strong);
    --cc-link-color: var(--accent);
  }

  /* Consent Modal - Bottom Bar Style, dezent */
  #cc-main .cm {
    border-radius: 12px 12px 0 0 !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3) !important;
    max-width: 100% !important;
    border: 1px solid var(--border-strong) !important;
    border-bottom: none !important;
  }

  /* Kleinere, dezentere Darstellung */
  #cc-main .cm__body {
    padding: 1rem 1.5rem !important;
  }

  #cc-main .cm__title {
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: var(--text-primary) !important;
  }

  #cc-main .cm__desc {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
    color: var(--text-muted) !important;
    margin-bottom: 0.75rem !important;
  }

  /* Buttons kompakt */
  #cc-main .cm__btn-group {
    gap: 0.5rem !important;
    flex-wrap: wrap !important;
  }

  #cc-main .cm__btn {
    padding: 0.5rem 1rem !important;
    font-size: 0.875rem !important;
    border-radius: 8px !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
  }

  #cc-main .cm__btn--accept-all {
    background: linear-gradient(135deg, #20D08A, #1BB377) !important;
    border: none !important;
    color: var(--accent-fg) !important; /* Navy on Mint — WCAG AA, theme-invariant */
  }

  #cc-main .cm__btn--accept-all:hover {
    background: linear-gradient(135deg, #1BB377, #149563) !important;
    transform: translateY(-1px) !important;
  }

  #cc-main .cm__btn--reject-all,
  #cc-main .cm__btn--settings {
    background: var(--surface-inset) !important;
    border: 1px solid var(--border-strong) !important;
    color: var(--text-primary) !important;
  }

  #cc-main .cm__btn--reject-all:hover,
  #cc-main .cm__btn--settings:hover {
    background: var(--surface-hover) !important;
  }

  /* Links */
  #cc-main a {
    color: var(--accent) !important;
    text-decoration: none !important;
  }

  #cc-main a:hover {
    text-decoration: underline !important;
  }

  /* Settings Modal - Modernes Design */
  #cc-main .pm {
    border-radius: 16px !important;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4) !important;
    border: 1px solid var(--border-strong) !important;
    max-width: 600px !important;
  }

  #cc-main .pm__header {
    border-bottom: 1px solid var(--border-strong) !important;
    padding: 1.25rem !important;
  }

  #cc-main .pm__title {
    font-size: 1.125rem !important;
    color: var(--text-primary) !important;
  }

  #cc-main .pm__body {
    padding: 1rem 1.25rem !important;
  }

  #cc-main .pm__section {
    border-radius: 8px !important;
    margin-bottom: 0.75rem !important;
    border: 1px solid var(--border-strong) !important;
  }

  #cc-main .pm__section-title-wrapper {
    padding: 0.75rem 1rem !important;
  }

  #cc-main .pm__section-title {
    font-size: 0.9375rem !important;
    color: var(--text-primary) !important;
  }

  #cc-main .pm__section-desc {
    font-size: 0.8125rem !important;
    color: var(--text-muted) !important;
    padding: 0 1rem 0.75rem !important;
  }

  /* Toggle Switch */
  #cc-main .pm__toggle {
    transform: scale(0.9) !important;
  }

  /* Footer */
  #cc-main .pm__footer {
    border-top: 1px solid var(--border-strong) !important;
    padding: 1rem 1.25rem !important;
    gap: 0.5rem !important;
  }

  /* Mobile Optimierungen */
  @media (max-width: 640px) {
    #cc-main .cm__body {
      padding: 0.875rem 1rem !important;
    }

    #cc-main .cm__btn-group {
      flex-direction: column !important;
    }

    #cc-main .cm__btn {
      width: 100% !important;
    }

    #cc-main .cm__desc {
      font-size: 0.8125rem !important;
    }
  }

  /* Animation */
  #cc-main .cm.cm--bottom {
    animation: slideUp 0.3s ease-out !important;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CookieConsentBanner: React.FC = () => {
  useEffect(() => {
    // Custom Styles einf¸gen
    const styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);

    // Consent-Cookie auf die registrierbare Domain heben (Cross-Subdomain-Consent).
    const consentCookieDomain = getConsentCookieDomain();

    // vanilla-cookieconsent initialisieren
    CookieConsent.run({
      // Consent-Cookie-Domain nur setzen, wenn ableitbar (sonst Host-only-Default).
      ...(consentCookieDomain ? { cookie: { domain: consentCookieDomain } } : {}),

      // GUI Optionen - dezent am unteren Rand
      guiOptions: {
        consentModal: {
          layout: 'bar',
          position: 'bottom',
          equalWeightButtons: false,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },

      // Cookie-Kategorien
      categories: {
        necessary: {
          enabled: true,
          readOnly: true // Kann nicht deaktiviert werden
        },
        analytics: {
          enabled: false, // Standardmäßig AUS (Opt-in erforderlich!)
          autoClear: {
            cookies: [
              { name: /^_ga/ },
              { name: '_gid' },
              { name: /^_gat/ },
              { name: '_gcl_au' },
              { name: 'ga-disable-G-YDZCCRPF7M' }
            ]
          }
        }
      },

      // Sprach-Konfiguration
      language: {
        default: 'de',
        autoDetect: 'browser',
        translations: {
          de: {
            consentModal: {
              title: '🍪 Cookie-Einstellungen',
              description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und unsere Website zu analysieren. <a href="/datenschutz" class="cc-link">Mehr erfahren</a>',
              acceptAllBtn: 'Alle akzeptieren',
              acceptNecessaryBtn: 'Nur notwendige',
              showPreferencesBtn: 'Einstellungen'
            },
            preferencesModal: {
              title: 'Cookie-Einstellungen',
              acceptAllBtn: 'Alle akzeptieren',
              acceptNecessaryBtn: 'Nur notwendige',
              savePreferencesBtn: 'Auswahl speichern',
              closeIconLabel: 'Schließen',
              sections: [
                {
                  title: 'Cookie-Nutzung',
                  description: 'Wir nutzen Cookies, um grundlegende Funktionen bereitzustellen und unseren Service zu verbessern. Sie können selbst entscheiden, welche Kategorien Sie zulassen möchten.'
                },
                {
                  title: 'Technisch notwendige Cookies',
                  description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich (z.B. Login, Spracheinstellungen) und können nicht deaktiviert werden.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analyse & Statistiken',
                  description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren. Die Daten werden anonymisiert und dienen zur Verbesserung unseres Angebots (Google Analytics).',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      description: 'Beschreibung',
                      expiration: 'Gültigkeit'
                    },
                    body: [
                      {
                        name: '_ga',
                        domain: 'faebill.com',
                        description: 'Google Analytics - Unterscheidet Nutzer',
                        expiration: '2 Jahre'
                      },
                      {
                        name: '_gid',
                        domain: 'faebill.com',
                        description: 'Google Analytics - Unterscheidet Nutzer',
                        expiration: '24 Stunden'
                      }
                    ]
                  }
                },
                {
                  title: 'Weitere Informationen',
                  description: 'Bei Fragen zu unserer Cookie-Richtlinie kontaktieren Sie uns unter <a class="cc-link" href="mailto:support@faebill.com">support@faebill.com</a> oder lesen Sie unsere <a class="cc-link" href="/datenschutz">Datenschutzerklärung</a>.'
                }
              ]
            }
          },
          en: {
            consentModal: {
              title: '🍪 Cookie Settings',
              description: 'We use cookies to enhance your experience and analyze our website. <a href="/datenschutz" class="cc-link">Learn more</a>',
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Necessary Only',
              showPreferencesBtn: 'Settings'
            },
            preferencesModal: {
              title: 'Cookie Settings',
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Necessary Only',
              savePreferencesBtn: 'Save Selection',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to provide basic functionality and improve our service. You can choose which categories to allow.'
                },
                {
                  title: 'Strictly Necessary Cookies',
                  description: 'These cookies are essential for basic website functions (e.g., login, language settings) and cannot be disabled.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analytics & Statistics',
                  description: 'These cookies help us understand how visitors interact with our website. Data is anonymized and used to improve our service (Google Analytics).',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      description: 'Description',
                      expiration: 'Expiration'
                    },
                    body: [
                      {
                        name: '_ga',
                        domain: 'faebill.com',
                        description: 'Google Analytics - Distinguishes users',
                        expiration: '2 years'
                      },
                      {
                        name: '_gid',
                        domain: 'faebill.com',
                        description: 'Google Analytics - Distinguishes users',
                        expiration: '24 hours'
                      }
                    ]
                  }
                },
                {
                  title: 'More Information',
                  description: 'For questions about our cookie policy, contact us at <a class="cc-link" href="mailto:support@faebill.com">support@faebill.com</a> or read our <a class="cc-link" href="/datenschutz">Privacy Policy</a>.'
                }
              ]
            }
          }
        }
      },

      // Event Callbacks für Google Consent Mode v2
      onFirstConsent: ({ cookie }) => {
        console.log('Cookie consent given for the first time');
        updateGoogleConsent(cookie);
      },

      onConsent: ({ cookie }) => {
        console.log('Cookie consent updated');
        updateGoogleConsent(cookie);
      },

      onChange: ({ cookie, changedCategories }) => {
        console.log('Cookie preferences changed:', changedCategories);
        updateGoogleConsent(cookie);
      }
    });

    // Cleanup
    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  return null; // Die Bibliothek rendert das Banner selbst
};

/**
 * Google Consent Mode v2 Update Funktion
 * Synchronisiert den Cookie-Consent-Status mit Google Analytics
 */
function updateGoogleConsent(cookie: { categories: string[] }) {
  const analyticsAccepted = cookie.categories.includes('analytics');

  // Google Consent Mode v2 Update
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': analyticsAccepted ? 'granted' : 'denied',
      'ad_storage': 'denied', // Immer denied da wir keine Ads nutzen
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });

    console.log(`Google Consent Mode updated: analytics_storage=${analyticsAccepted ? 'granted' : 'denied'}`);
  }

  // Analytics-Service als EINZIGER Aktivierungspfad (ersetzt die vormals hier
  // direkten window.enable/disableGoogleAnalytics-Aufrufe): der Service ruft
  // window.enableGoogleAnalytics() intern selbst auf UND flusht die vor Consent
  // gequeueten Events. Zuvor blieb analyticsService.isEnabled=false → Queue
  // wurde nie geleert (Bruch B2, architecture-ga4-funnel.md §2.1).
  if (analyticsAccepted) {
    analyticsService.enableAnalytics();
  } else {
    analyticsService.disableAnalytics();
  }
}

// Hilfsfunktionen für externen Zugriff
export const showCookieSettings = () => {
  CookieConsent.showPreferences();
};

export const acceptAllCookies = () => {
  CookieConsent.acceptCategory('all');
};

export const rejectAllCookies = () => {
  CookieConsent.acceptCategory([]);
};

export const hasAnalyticsConsent = (): boolean => {
  return CookieConsent.acceptedCategory('analytics');
};

export default CookieConsentBanner;
